// 输入框里的「思考讨论」：开关、挂上的资料、「过去的你」的提示、开始讨论。新对话页和对话页共用。

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp } from 'antd'
import { createThink, listThinks, thinkMeta } from '../../api/think'
import { listJudgments } from '../../api/insights'
import { getKnowledge } from '../../api/kb'
import type { Knowledge } from '../../api/types'
import { thinkScope, useAllScopeKbs, type ThinkMode } from '../chat/Composer'
import type { Scope } from '../chat/prefs'
import { alike } from '../../lib/alike'

/** 从别处带来的：知识库 AI 侧栏的「思考讨论」、观点里的「拿它再讨论一次」。 */
export interface ThinkCarry {
  mode?: 'think'
  topic?: string
  contextKnowledgeIds?: string[]
}

/** 服务端字面上够像就一定带上（另一条路是检索排名，网页这边算不出来，所以只在够像时才提示）。 */
const ALIKE = 0.6

const md = (iso: string) => {
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? `${d.getMonth() + 1}/${d.getDate()}` : ''
}

function usePastHint(topic: string, on: boolean) {
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(topic.trim()), 300)
    return () => clearTimeout(t)
  }, [topic])
  const thinks = useQuery({ queryKey: ['thinks'], queryFn: listThinks, enabled: on })
  const judgments = useQuery({ queryKey: ['insight-judgments'], queryFn: listJudgments, enabled: on })
  return useMemo(() => {
    if (!on || q.length < 4) return null
    const past = [
      ...(thinks.data ?? []).filter((s) => s.phase === 'closed').map((s) => ({ topic: s.topic, at: s.updatedAt })),
      ...(judgments.data ?? []).filter((j) => j.saved && j.source !== 'think').map((j) => ({ topic: j.topic, at: j.createdAt })),
    ]
    const best = past.map((p) => ({ ...p, like: alike(q, p.topic) })).filter((p) => p.like >= ALIKE).sort((a, b) => b.like - a.like)[0]
    return best ? `你 ${md(best.at)} 想过类似的问题，「过去的你」会一起来` : null
  }, [on, q, thinks.data, judgments.data])
}

export function useThinkMode(carry?: ThinkCarry | null) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const kbs = useAllScopeKbs() ?? []
  const meta = useQuery({ queryKey: ['think-meta'], queryFn: thinkMeta, staleTime: 10 * 60_000 })
  const [on, setOn] = useState(carry?.mode === 'think')
  const [attached, setAttached] = useState<Knowledge[]>([])
  const [text, setText] = useState(carry?.topic ?? '')

  useEffect(() => {
    const ids = carry?.contextKnowledgeIds ?? []
    if (!ids.length) return
    let live = true
    void Promise.all(ids.map((id) => getKnowledge(id).catch(() => null))).then((docs) => live && setAttached(docs.filter((d): d is Knowledge => !!d)))
    return () => {
      live = false
    }
  }, [])

  const hint = usePastHint(text, on)
  const start = useMutation({
    mutationFn: ({ topic, scope }: { topic: string; scope: Scope }) => {
      const s = thinkScope(scope)
      return createThink({ topic, knowledgeBaseIds: s.kind === 'kb' ? [s.kbId] : kbs.map((k) => k.id), contextKnowledgeIds: attached.map((k) => k.id) })
    },
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['thinks'] })
      navigate(`/think/${s.id}`)
    },
    onError: (e) => void message.error(e.message),
  })

  const mode: ThinkMode = { on, onToggle: setOn, attached, onAttached: setAttached, max: meta.data?.limits.maxContext ?? 6, hint }
  return { mode, start: (topic: string, scope: Scope) => start.mutate({ topic: topic.trim(), scope }), starting: start.isPending, initialText: carry?.topic, onText: setText }
}
